import React, { useState } from 'react'
import {
  XMarkIcon,
  QrCodeIcon,
  ClipboardDocumentIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'

interface CreateHubnaiChargePixModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ChargeResult {
  id: number
  hubnai_charge_id: string
  amount: number
  payment_method: string
  status: string
  pix_qr_code_url?: string
  pix_code?: string
  expires_at?: string
}

const CreateHubnaiChargePixModal: React.FC<CreateHubnaiChargePixModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [chargeResult, setChargeResult] = useState<ChargeResult | null>(null)
  const [copied, setCopied] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    amount: '', // Em reais
    description: '',
    customer_name: '',
    customer_email: '',
    customer_document: '',
    expires_in: '86400' // 24 horas em segundos
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(parseFloat(formData.amount) * 100)

      // Preparar dados da cobrança conforme novo backend
      const chargeData: any = {
        amount: amountInCents,
        orderId: `ORDER-${Date.now()}`, // Gerar orderId único
        expiration: formData.expires_in, // em segundos
        description: formData.description
      }

      // Adicionar dados do cliente se fornecidos (KYC opcional)
      if (formData.customer_name && formData.customer_email && formData.customer_document) {
        chargeData.customer = {
          name: formData.customer_name,
          documentNumber: formData.customer_document.replace(/\D/g, ''),
          email: formData.customer_email
        }
      }

      console.log('📤 Criando cobrança PIX:', chargeData)

      const result = await hubnaiAPI.createPixCharge(chargeData)

      console.log('✅ Cobrança PIX criada:', result)

      // Adaptar resultado para o formato esperado pelo componente
      setChargeResult({
        id: 0, // Não temos ID local ainda
        hubnai_charge_id: result.paymentId,
        amount: result.amount,
        payment_method: 'pix',
        status: result.status === 1 ? 'pending' : 'paid',
        pix_qr_code_url: result.qrCodeUrl,
        pix_code: result.qrCodeContent,
        expires_at: result.dueDate
      })

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar cobrança PIX:', error)
      alert('Erro ao criar cobrança PIX: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const copyPixCode = () => {
    if (chargeResult && chargeResult.pix_code) {
      navigator.clipboard.writeText(chargeResult.pix_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const handleClose = () => {
    setChargeResult(null)
    setFormData({
      amount: '',
      description: '',
      customer_name: '',
      customer_email: '',
      customer_document: '',
      expires_in: '86400'
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra o QR code
  if (chargeResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <QrCodeIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Cobrança PIX Criada!</h2>
                  <p className="text-sm text-gray-500">ID: {chargeResult.hubnai_charge_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* QR Code Image */}
            {chargeResult.pix_qr_code_url && (
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-lg border-2 border-purple-500">
                  <img
                    src={chargeResult.pix_qr_code_url}
                    alt="QR Code PIX"
                    className="w-64 h-64"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Escaneie o QR Code para pagar
                </p>
              </div>
            )}

            {/* PIX Copy-Paste Code */}
            {chargeResult.pix_code && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código PIX Copia e Cola
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chargeResult.pix_code}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                  />
                  <button
                    onClick={copyPixCode}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  >
                    {copied ? (
                      <>
                        <CheckIcon className="w-5 h-5" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="w-5 h-5" />
                        Copiar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Info */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-semibold text-gray-900">{formatCurrency(chargeResult.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-purple-700 uppercase">{chargeResult.status}</span>
              </div>
              {chargeResult.expires_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Expira em:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(chargeResult.expires_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>

            {/* Instrução */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-800">
                💡 <strong>Importante:</strong> O pagamento será sincronizado automaticamente após confirmação.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar cobrança
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <QrCodeIcon className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Nova Cobrança PIX</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dados da Cobrança */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados da Cobrança</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="29.90"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ex: Pagamento de serviço"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tempo de Expiração
                </label>
                <select
                  name="expires_in"
                  value={formData.expires_in}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="3600">1 hora</option>
                  <option value="21600">6 horas</option>
                  <option value="43200">12 horas</option>
                  <option value="86400">24 horas (padrão)</option>
                  <option value="172800">48 horas</option>
                </select>
              </div>
            </div>
          </div>

          {/* Dados do Cliente (Opcional) */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Dados do Cliente</h3>
            <p className="text-sm text-gray-500 mb-4">Opcional - Preencha para vincular a um cliente</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="João da Silva"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="joao@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ
                </label>
                <input
                  type="text"
                  name="customer_document"
                  value={formData.customer_document}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Criando...
                </>
              ) : (
                <>
                  <QrCodeIcon className="w-5 h-5" />
                  Gerar Cobrança PIX
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateHubnaiChargePixModal
